import React from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { mkConfig, generateCsv, download } from "export-to-csv";
import { Box, Button } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Navigate, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import useLoader from "../Hooks/useLoader";
import SnackBar from "../component/SnackBar";
import { CustomTableFunction } from "../Table/Table";

const HomeTable = (props) => {
  const [data, setData] = useState(props.data);
  const [showTable, setShowTable] = useState(false);
  const [validationErrors] = useState({});
  const [loader, showLoader, hideLoader] = useLoader({
    heightOfLoader: "132vh",
    marginTopLoader: "60%",
  });
  const navigate = useNavigate();
  const alertRef = useRef();
  const [colFilter, setColFilter] = useState([]);

  useEffect(() => {
    try {
      setShowTable(props.tableValue);
    } catch (error) {}
  }, [props.tableValue]);
  useEffect(() => {
    try {
      setData(props.data);
    } catch (error) {}
  }, [props.data]);
  const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
  });
  const handleExportData = async () => {
    try {
      showLoader();

      const excludedColumns = ["_id"];

      const cleanedData = props.data.map((row) => {
        const newRow = {};
        Object.keys(row).forEach((key) => {
          if (!excludedColumns.includes(key)) {
            const newKey = key
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ");
            newRow[newKey] = row[key];
          }
        });
        return newRow;
      });

      const csv = generateCsv(csvConfig)(cleanedData);
      download(csvConfig)(csv);

      hideLoader();
    } catch (error) {
      hideLoader();
    }
  };
  const handleExportRows = async (rows) => {
    try {
      showLoader();
      const csv = generateCsv(csvConfig)(rows);
      download(csvConfig)(csv);
      hideLoader();
    } catch (error) {
      hideLoader();
    }
  };

  const formateDateString = (dateVar) => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan",
      "Feb",
      "March",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const date = new Date(dateVar.getTime());

    const day = days[date.getDay()];
    const month = months[date.getMonth()];

    const dayOfMonth = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear());
    const hour = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    return `${day}, ${dayOfMonth} ${month} ${year} ${hour}:${min}:${seconds} CST`;
  };

  let columns = useMemo(
    () => [
      {
        accessorKey: "Processed_Date",
        header: "Day & Date",
        Cell: ({ cell, row }) => {
          const dateValue = row.original.Processed_Date;
          const displayDate = new Date(dateValue).toLocaleDateString("en-GB", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          });

          return (
            <a
              href={`/detail?Processed_Date=${dateValue}`}
              style={{ color: "#007BFF", textDecoration: "underline" }}
            >
              {displayDate}
            </a>
          );
        },
        filterVariant: "date-range",
        filterFn: (row, columnId, filterValue) => {
          const rowValue = new Date(row.getValue(columnId));
          const [start, end] = filterValue;
          const startDate = start ? new Date(start) : null;
          const endDate = end ? new Date(end) : null;
          if (!start && !end) return true;
          if (startDate) startDate.setHours(0, 0, 0, 0);
          if (endDate) endDate.setHours(23, 59, 59, 999);
          if (startDate && !endDate) return rowValue >= startDate;
          if (!startDate && endDate) return rowValue <= endDate;
          if (startDate && endDate)
            return rowValue >= startDate && rowValue <= endDate;
        },
        size: 30,
        muiTableHeadCellProps: { sx: { color: "#002677" } },
        enableEditing: false,
        muiFilterTextFieldProps: { placeholder: "Day & Date" },
        // sortDescFirst: false,
      },
      {
        accessorKey: "Total",
        header: "Contracts Processed",
        minSize: 60,
        maxSize: 60,
        size: 60,
        muiTableHeadCellProps: { sx: { color: "#002677" } }, //custom props
        enableEditing: false,
        muiFilterTextFieldProps: { placeholder: "Contracts Processed" },
        muiTableBodyCellProps: {
          align: "right",
        },
      },
      {
        accessorKey: "Success_Count",
        header: "Successfully Processed",
        minSize: 60,
        maxSize: 60,
        size: 60,
        muiTableHeadCellProps: { sx: { color: "#002677" } }, //custom props
        enableEditing: false,
        muiFilterTextFieldProps: { placeholder: "Successfully Processed" },
        muiTableBodyCellProps: {
          align: "right",
        },
      },
      {
        accessorKey: "Failure_Count",
        header: "Failed Contracts",
        minSize: 60,
        maxSize: 60,
        size: 60,
        muiTableHeadCellProps: { sx: { color: "#002677" } }, //custom props
        enableEditing: false,
        muiFilterTextFieldProps: { placeholder: "Failed Contracts" },
        muiTableBodyCellProps: {
          align: "right",
        },
      },
      //  {
      //   accessorKey:'_id',
      //   header: 'Id',
      //   minSize: 60,
      //   maxSize: 60,
      //   size: 60,
      //   muiTableHeadCellProps: { sx: { color: "#002677" }  }, //custom props
      //   enableEditing: false,
      //   muiFilterTextFieldProps: { placeholder: 'failed' },
      // },
    ],
    [validationErrors]
  );

  const tableProperties = {
    filterFns: {
      myCustomFilterFn: (row, id, filterValue) => {
        let found = false;
        const visibleColumns = table
          .getAllLeafColumns()
          .filter((column) => column.getIsVisible());

        for (const value of visibleColumns) {
          const valueId = value.id;

          if (valueId === "Processed_Date") {
            const dateValue = row.original.Processed_Date;

            const Processed_Date = new Date(dateValue).toLocaleDateString(
              "en-GB",
              {
                weekday: "long",
                day: "2-digit",
                month: "long",
                year: "numeric",
              }
            );
            //const lastUpdationDate= row.original.claim_completion_last_update_date;
            if (
              Processed_Date.toLowerCase().includes(filterValue.toLowerCase())
            ) {
              found = true;
            }
          }

          if (
            String(row.original[valueId])
              .toLowerCase()
              .trim()
              .includes(filterValue.toLowerCase().trim())
          ) {
            found = true;
          }
        }
        return found;
      },
    },
     initialState: {
      sorting: [
        { id: 'Processed_Date', desc: true }, //sort by state in ascending order by default
      ],
    },

    globalFilterFn: "myCustomFilterFn",
    enableGlobalFilter: true,
    enableGrouping: false,
    // initialState: { showColumnFilters: true },
    groupedColumnMode: "remove",
    enableSorting: true,
    editDisplayMode: "table",
    // muiTableContainerProps: { sx: { } },
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    muiTableHeadRowProps: { sx: { backgroundColor: "transparent" } },
    muiTopToolbarProps: { sx: { backgroundColor: "transparent" } },
    muiTableFooterRowProps: { sx: { backgroundColor: "transparent" } },
    muiBottomToolbarProps: { sx: { backgroundColor: "transparent" } },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "none",
        backgroundColor: "transparent",
      },
    },
    muiTableProps: {
      sx: {
        borderCollapse: "collapse",
      },
    },
    // columnFilterDisplayMode: 'popover',
    muiTableBodyRowProps: {
      sx: (theme) => ({
        borderSpacing: "0 10px",
        borderBottom: "5px solid transparent",
      }),
    },
    muiPaginationProps: {
      color: "primary",
      shape: "circular",
      showRowsPerPage: false,
      variant: "outlined",
    },
    paginationDisplayMode: "pages",

    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            // padding: '8px',
            flexWrap: "wrap",
          }}
        >
          <Button
            //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
            onClick={handleExportData}
            startIcon={<FileDownloadIcon />}
          >
            Export All Data
          </Button>
        </Box>
      );
    },
  };

  const table = CustomTableFunction({
    columns: columns,
    data: data,
    tableProperties: tableProperties,
  });

  return (
    <div>
      {loader}
      {showTable && <MaterialReactTable table={table} />}
      <SnackBar ref={alertRef} />
    </div>
  );
};

export default HomeTable;
